import { computed, ref } from 'vue'

import { formatDate, formatDateRange, formatYYYYMMDD } from '@/utils/Utility.ts'
import { getDatesInRange, inferCampaignAggregateType } from '@/utils/rateConnectUtils.ts'

const bookingSortColumn = ref('departureDate')
const selectedPeriod = ref('last_30_days')
const selectedBookingPeriod = ref('last_30_days')
const selectedMarket = ref('all')
const selectedDisplayType = ref('table')
const selectedGraph = ref('clicks')
const selectedDateType = ref('departureDate')
const appliedFiltersAmount = ref(0)
const selectedAggregation = ref('DAILY')
const selectedStatus = ref(['CONFIRMED', 'CANCELLED', 'COMPLETED'])
const selectedColumns = ref(null)
const loadedElementsOverviewTable = ref(6)
const comparisonStartDate = ref(null)
const comparisonEndDate = ref(null)
const bookingComparisonStartDate = ref(null)
const bookingComparisonEndDate = ref(null)

const endDate = computed(() => {
  const newEndDate = new Date()
  newEndDate.setDate(newEndDate.getDate() - 1)
  return newEndDate
})
const startDate = computed(() => {
  if (!endDate.value) return null

  switch (selectedPeriod.value) {
    case 'last_14_days':
      return new Date(endDate.value.getTime() - 13 * 24 * 60 * 60 * 1000)

    case 'current_month':
      return new Date(endDate.value.getFullYear(), endDate.value.getMonth(), 1)

    case 'last_30_days':
      return new Date(endDate.value.getTime() - 29 * 24 * 60 * 60 * 1000)

    case 'last_3_months':
      return new Date(endDate.value.getTime() - 89 * 24 * 60 * 60 * 1000)

    case 'last_6_months':
      return new Date(endDate.value.getTime() - 179 * 24 * 60 * 60 * 1000)

    case 'last_12_months':
      return new Date(endDate.value.getTime() - 364 * 24 * 60 * 60 * 1000)

    default:
      return new Date(endDate.value.getTime() - 13 * 24 * 60 * 60 * 1000)
  }
})

const calculateBookingDateRange = periodId => {
  const today = new Date()
  const currentYear = today.getFullYear()
  const currentMonth = today.getMonth() // 0-11

  let startDate, endDate, compStart, compEnd

  // Handle days
  if (periodId === 'last_30_days') {
    const days = 30
    endDate = new Date()
    endDate.setDate(endDate.getDate() - 1) // Yesterday
    startDate = new Date(endDate)
    startDate.setDate(startDate.getDate() - days + 1)
    // Comparison: 30 days before that
    compEnd = new Date(startDate)
    compEnd.setDate(compEnd.getDate() - 1)
    compStart = new Date(compEnd)
    compStart.setDate(compStart.getDate() - days + 1)
    return { startDate, endDate, comparisonStartDate: compStart, comparisonEndDate: compEnd }
  }

  if (periodId === 'last_90_days') {
    const days = 90
    endDate = new Date()
    endDate.setDate(endDate.getDate() - 1) // Yesterday
    startDate = new Date(endDate)
    startDate.setDate(startDate.getDate() - days + 1)
    // Comparison: 90 days before that
    compEnd = new Date(startDate)
    compEnd.setDate(compEnd.getDate() - 1)
    compStart = new Date(compEnd)
    compStart.setDate(compStart.getDate() - days + 1)
    return { startDate, endDate, comparisonStartDate: compStart, comparisonEndDate: compEnd }
  }

  if (periodId === 'next_30_days') {
    const days = 30
    startDate = new Date() // Today
    endDate = new Date()
    endDate.setDate(endDate.getDate() + days - 1)
    // Comparison: previous 30 days (ending yesterday)
    compEnd = new Date()
    compEnd.setDate(compEnd.getDate() - 1) // Yesterday
    compStart = new Date(compEnd)
    compStart.setDate(compStart.getDate() - days + 1)
    return { startDate, endDate, comparisonStartDate: compStart, comparisonEndDate: compEnd }
  }

  if (periodId === 'next_90_days') {
    const days = 90
    startDate = new Date() // Today
    endDate = new Date()
    endDate.setDate(endDate.getDate() + days - 1)
    // Comparison: previous 90 days (ending yesterday)
    compEnd = new Date()
    compEnd.setDate(compEnd.getDate() - 1) // Yesterday
    compStart = new Date(compEnd)
    compStart.setDate(compStart.getDate() - days + 1)
    return { startDate, endDate, comparisonStartDate: compStart, comparisonEndDate: compEnd }
  }

  // Handle months
  switch (periodId) {
    case 'current_month':
      // Current month: 1st of this month to last day of this month
      startDate = new Date(currentYear, currentMonth, 1)
      endDate = new Date(currentYear, currentMonth + 1, 0) // Last day of current month
      // Comparison: previous month (complete)
      compStart = new Date(currentYear, currentMonth - 1, 1)
      compEnd = new Date(currentYear, currentMonth, 0)
      break

    case 'last_1_month':
      // Last 1 month: previous complete month
      startDate = new Date(currentYear, currentMonth - 1, 1)
      endDate = new Date(currentYear, currentMonth, 0) // Last day of previous month
      // Comparison: month before that
      compStart = new Date(currentYear, currentMonth - 2, 1)
      compEnd = new Date(currentYear, currentMonth - 1, 0)
      break

    case 'last_3_months':
      // Last 3 months: 3 complete months before current
      startDate = new Date(currentYear, currentMonth - 3, 1)
      endDate = new Date(currentYear, currentMonth, 0) // Last day of previous month
      // Comparison: 3 months before that
      compStart = new Date(currentYear, currentMonth - 6, 1)
      compEnd = new Date(currentYear, currentMonth - 3, 0)
      break

    case 'last_6_months':
      // Last 6 months: 6 complete months before current
      startDate = new Date(currentYear, currentMonth - 6, 1)
      endDate = new Date(currentYear, currentMonth, 0)
      // Comparison: 6 months before that
      compStart = new Date(currentYear, currentMonth - 12, 1)
      compEnd = new Date(currentYear, currentMonth - 6, 0)
      break

    case 'last_12_months':
      // Last 12 months: 12 complete months before current
      startDate = new Date(currentYear, currentMonth - 12, 1)
      endDate = new Date(currentYear, currentMonth, 0)
      // Comparison: 12 months before that
      compStart = new Date(currentYear, currentMonth - 24, 1)
      compEnd = new Date(currentYear, currentMonth - 12, 0)
      break

    case 'next_1_month':
      // Next 1 month: next complete month
      startDate = new Date(currentYear, currentMonth + 1, 1)
      endDate = new Date(currentYear, currentMonth + 2, 0)
      // Comparison: current month
      compStart = new Date(currentYear, currentMonth, 1)
      compEnd = new Date(currentYear, currentMonth + 1, 0)
      break

    case 'next_3_months':
      // Next 3 months: 3 complete months ahead
      startDate = new Date(currentYear, currentMonth + 1, 1)
      endDate = new Date(currentYear, currentMonth + 4, 0)
      // Comparison: previous 3 complete months (ending this month)
      compStart = new Date(currentYear, currentMonth - 2, 1)
      compEnd = new Date(currentYear, currentMonth + 1, 0)
      break

    case 'next_6_months':
      // Next 6 months: 6 complete months ahead
      startDate = new Date(currentYear, currentMonth + 1, 1)
      endDate = new Date(currentYear, currentMonth + 7, 0)
      // Comparison: previous 6 complete months (ending this month)
      compStart = new Date(currentYear, currentMonth - 5, 1)
      compEnd = new Date(currentYear, currentMonth + 1, 0)
      break

    default:
      // Default to last 30 days
      endDate = new Date()
      endDate.setDate(endDate.getDate() - 1) // Yesterday
      startDate = new Date(endDate)
      startDate.setDate(startDate.getDate() - 30 + 1)
      // Comparison: 30 days before that
      compEnd = new Date(startDate)
      compEnd.setDate(compEnd.getDate() - 1)
      compStart = new Date(compEnd)
      compStart.setDate(compStart.getDate() - 30 + 1)
      break
  }

  return { startDate, endDate, comparisonStartDate: compStart, comparisonEndDate: compEnd }
}

const bookingStartDate = computed(() => {
  const result = calculateBookingDateRange(selectedBookingPeriod.value)
  bookingComparisonStartDate.value = result.comparisonStartDate
  bookingComparisonEndDate.value = result.comparisonEndDate
  return result.startDate
})

const bookingEndDate = computed(() => {
  const result = calculateBookingDateRange(selectedBookingPeriod.value)
  return result.endDate
})

export function useRateConnectPerformance() {
  const resetData = () => {
    selectedPeriod.value = 'last_30_days'
    selectedBookingPeriod.value = 'last_30_days'
    selectedMarket.value = 'all'
    selectedGraph.value = 'clicks'
    selectedDisplayType.value = 'table'
    selectedAggregation.value = 'DAILY'
    selectedDateType.value = 'departureDate'
    bookingSortColumn.value = 'departureDate'
    appliedFiltersAmount.value = 0
    selectedStatus.value = ['CONFIRMED', 'CANCELLED', 'COMPLETED']
  }

  const resetFilters = () => {
    selectedPeriod.value = 'last_30_days'
    selectedBookingPeriod.value = 'last_30_days'
    selectedMarket.value = 'all'
    selectedGraph.value = 'clicks'
    selectedAggregation.value = 'DAILY'
    selectedDateType.value = 'departureDate'
    bookingSortColumn.value = 'departureDate'
    selectedStatus.value = ['CONFIRMED', 'CANCELLED', 'COMPLETED']
    appliedFiltersAmount.value = 0
  }

  const yearlyStartDate = computed(() => {
    const yearlyStartDate = new Date()
    yearlyStartDate.setDate(endDate.value.getDate() - 365)
    return formatYYYYMMDD(yearlyStartDate, '-')
  })

  const formattedEndDate = computed(() => (endDate.value ? formatYYYYMMDD(endDate.value, '-') : ''))
  const formattedStartDate = computed(() => (startDate.value ? formatYYYYMMDD(startDate.value, '-') : ''))
  const formattedComparisonEndDate = computed(() =>
    comparisonEndDate.value ? formatYYYYMMDD(comparisonEndDate.value, '-') : ''
  )
  const formattedComparisonStartDate = computed(() =>
    comparisonStartDate.value ? formatYYYYMMDD(comparisonStartDate.value, '-') : ''
  )

  const formattedBookingStartDate = computed(() =>
    bookingStartDate.value ? formatYYYYMMDD(bookingStartDate.value, '-') : ''
  )
  const formattedBookingEndDate = computed(() =>
    bookingEndDate.value ? formatYYYYMMDD(bookingEndDate.value, '-') : ''
  )

  const formattedBookingComparisonStartDate = computed(() =>
    bookingComparisonStartDate.value ? formatYYYYMMDD(bookingComparisonStartDate.value, '-') : ''
  )
  const formattedBookingComparisonEndDate = computed(() =>
    bookingComparisonEndDate.value ? formatYYYYMMDD(bookingComparisonEndDate.value, '-') : ''
  )

  return {
    resetData,
    selectedDateType,
    resetFilters,
    selectedPeriod,
    selectedBookingPeriod,
    selectedMarket,
    selectedGraph,
    startDate,
    yearlyStartDate,
    endDate,
    formattedStartDate,
    formattedEndDate,
    selectedDisplayType,
    selectedAggregation,
    appliedFiltersAmount,
    selectedStatus,
    selectedColumns,
    bookingSortColumn,
    loadedElementsOverviewTable,
    formattedComparisonEndDate,
    formattedComparisonStartDate,
    formattedBookingStartDate,
    formattedBookingEndDate,
    formattedBookingComparisonStartDate,
    formattedBookingComparisonEndDate,
  }
}

export function usePrettyStats(statsData, lastUpdated, language) {
  function checkDate(date) {
    return new Date(date.startDate) <= new Date(lastUpdated?.value)
  }

  const dateFormatOptions = computed(() => {
    const optionsMap = {
      DAILY: { weekday: 'short', month: 'short', day: 'numeric' },
      WEEKLY: { day: '2-digit', month: 'short' },
      MONTHLY: { month: 'short', year: 'numeric' },
    }
    return optionsMap[selectedAggregation.value] || {}
  })

  const campaignTypes = computed(() => {
    if (statsData.value.length === 0) {
      const fakeDates = getDatesInRange(startDate.value, endDate.value)
      return fakeDates.map(() => null)
    } else {
      return statsData.value?.map(campaign => inferCampaignAggregateType(campaign))
    }
  })
  const categories = computed(() => {
    if (!statsData.value || statsData.value.length === 0) {
      const fakeDates = getDatesInRange(startDate.value, endDate.value)
      return fakeDates.map(item => formatDate(item, language.value, dateFormatOptions.value))
    }
    return statsData.value.map(d => {
      if (selectedAggregation.value === 'DAILY' || selectedAggregation.value === 'MONTHLY') {
        return formatDate(d.startDate, language.value, dateFormatOptions.value)
      } else {
        return formatDateRange(d.startDate, d.endDate, language.value, dateFormatOptions.value)
      }
    })
  })
  const tickPositions = computed(() => {
    if (statsData.value.length === 0) {
      const fakeDates = getDatesInRange(startDate.value, endDate.value)

      return fakeDates.map((_, index) => {
        return index
      })
    } else {
      return statsData.value?.map((d, index) => {
        if (selectedAggregation.value === 'DAILY') {
          return new Date(d.startDate).getDay() === 1 ? index : undefined
        }
        return index
      })
    }
  })

  const bookings = computed(() => {
    const bookingsNames = ['partnerClicks', 'bookings', 'click2BookRatio', 'cancelledBookings']
    const bookingsObj = {}
    bookingsNames.forEach(name => {
      switch (name) {
        case 'click2BookRatio':
          bookingsObj[name] = statsData.value?.filter(checkDate).map(d => d[name] / 100)
          break
        default:
          bookingsObj[name] = statsData.value?.filter(checkDate).map(d => d[name] || 0)
      }
    })
    return bookingsObj
  })

  const visits = computed(() => {
    const visitsNames = ['totalImpressions', 'partnerClicks', 'cpc', 'clickThroughRate', 'unavailabilityScore']
    const visitsObj = {}
    visitsNames.forEach(name => {
      switch (name) {
        case 'clickThroughRate':
          visitsObj[name] = statsData.value?.filter(checkDate).map(d => d[name] / 100)
          break
        case 'cpc':
          visitsObj[name] = statsData.value?.filter(checkDate).map(d => (d[name] === null ? null : d[name] / 100))
          break
        case 'unavailabilityScore':
          visitsObj[name] = statsData.value?.filter(checkDate).map(d => (d[name] === null ? -0.1 : d[name]))
          break
        default:
          visitsObj[name] = statsData.value?.filter(checkDate).map(d => d[name])
      }
    })
    return visitsObj
  })

  const revenue = computed(() => {
    const revenueNames = ['bookings', 'bookingRevenue', 'avgBookingRevenue', 'bookingRefundedRevenue']
    const revenueObj = {}
    revenueNames.forEach(name => {
      switch (name) {
        case 'bookingRevenue':
        case 'avgBookingRevenue':
          revenueObj[name] = statsData.value?.filter(checkDate).map(d => d[name] / 100)
          break
        case 'bookingRefundedRevenue':
          revenueObj[name] = statsData.value?.filter(checkDate).map(d => (d[name] ? d[name] / 100 : 0))
          break
        default:
          revenueObj[name] = statsData.value?.filter(checkDate).map(d => d[name])
      }
    })
    return revenueObj
  })

  const cpa = computed(() => {
    const cpaNames = ['costs', 'bookingRevenue', 'cpa']
    const cpaObj = {}
    cpaNames.forEach(name => {
      switch (name) {
        case 'cpa':
        case 'costs':
        case 'bookingRevenue':
          cpaObj[name] = statsData.value?.filter(checkDate).map(d => Number(Math.round(d[name] / 100 + 'e2') + 'e-2'))
          break
        default:
          cpaObj[name] = statsData.value?.filter(checkDate).map(d => d[name])
      }
    })
    return cpaObj
  })

  const commissionCosts = computed(() => {
    const commissionCostNames = ['commissionCost', 'bookingRevenue', 'cpaLevel']
    const commissionCostsObj = {}
    commissionCostNames.forEach(name => {
      switch (name) {
        case 'commissionCost':
        case 'bookingRevenue':
          commissionCostsObj[name] = statsData.value?.filter(checkDate).map(d => d[name] / 100)
          break
        case 'cpaLevel':
          commissionCostsObj[name] = statsData.value?.filter(checkDate).map(d => d[name])
          break
        default:
          commissionCostsObj[name] = statsData.value?.filter(checkDate).map(d => d[name])
      }
    })
    return commissionCostsObj
  })

  return {
    bookings,
    visits,
    revenue,
    cpa,
    commissionCosts,
    campaignTypes,
    categories,
    tickPositions,
  }
}
